import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useShopLoader } from '../hooks/useShopLoader';
import { useBaristaShopLoader } from '../hooks/useBaristaShopLoader';

const RoleRoute = ({ children, allowedRoles = [], routeType = 'owner' }) => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  
  // Only load shop data for non-admin users
  const ownerShopData = useShopLoader();
  const baristaShopData = useBaristaShopLoader();
  
  const shopData = routeType === 'barista' ? baristaShopData : ownerShopData;
  const isAdminUser = user?.role === 'ADMIN' || user?.email === 'admin@coffeecloud.com';
  const { shop, loading: shopLoading } = isAdminUser ? { shop: null, loading: false } : shopData;
  
  if (authLoading || (shopLoading && !isAdminUser)) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a15507] mx-auto mb-4"></div>
          <p className="text-gray-300">
            {user?.role === 'ADMIN' ? 'Loading admin panel...' : 'Loading your coffee shop...'}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    const isAdminEmail = user?.email === 'admin@coffeecloud.com';
    
    if (allowedRoles.includes('ADMIN') && isAdminEmail) {
      return children;
    }
    
    let redirectPath;
    switch (user?.role) {
      case 'ADMIN':
        redirectPath = '/admin';
        break;
      case 'OWNER':
        redirectPath = '/dashboard';
        break;
      case 'BARISTA':
        redirectPath = '/barista';
        break;
      default:
        redirectPath = '/login';
    }
    return <Navigate to={redirectPath} replace />;
  }

  // Skip shop validation for admin users or admin email
  if (!isAdminUser) {
    // For baristas, they should always have access to their linked shop
    // For owners, they need to have created a shop
    if (user?.role === 'OWNER' && !shop) {
      return <Navigate to="/onboarding" replace />;
    }

    // For baristas, if no shop is found, it means they're not properly linked
    if (user?.role === 'BARISTA' && !shop) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] flex items-center justify-center">
          <div className="text-center text-white">
            <div className="text-red-400 mb-4">
              <h2 className="text-xl font-bold">Access Error</h2>
              <p className="text-gray-300 mt-2">You are not linked to any coffee shop</p>
              <p className="text-sm text-gray-400 mt-1">Please contact your shop owner</p>
            </div>
          </div>
        </div>
      );
    }
  }

  return children;
};

export default RoleRoute; 